import { Link, useLocation } from 'react-router-dom';
import { Lock, PackageOpen } from 'lucide-react';
import { esEmpresaDemo } from '@/features/mini-prixia/empresa-demo';
import { VolverConElPlanCompleto } from '@/features/mini-prixia/components/volver-con-el-plan-completo';
import { PANTALLA_SIN_MODULOS, usePrimeraPantalla } from '@/router/primera-pantalla';

/**
 * Lo que se muestra cuando alguien llega a un módulo al que no puede entrar,
 * incluido el acceso escribiendo la dirección.
 *
 * 🔴 **Por qué hay dos motivos y no uno.** Antes el texto era siempre *"Tu rol no
 * tiene acceso… contactá a tu administrador"*. Eso está bien para un caso y manda
 * a la persona equivocada en el otro: si el módulo no está en el plan de la
 * empresa, su administrador **no lo puede resolver** — por más que lo intente, no
 * hay una casilla que pueda marcar. Mandarlo ahí le hace perder el tiempo a dos
 * personas.
 */
export type MotivoDeBloqueo =
  /** El cliente marcó este módulo como bloqueado para este rol. */
  | 'rol'
  /** La empresa no compró este módulo. Solo lo resuelve la plataforma. */
  | 'no-incluido'
  /** La ruta no está declarada en el registro de rutas (cerrado por defecto). */
  | 'sin-registrar';

interface LockedModuleProps {
  motivo?: MotivoDeBloqueo;
}

const TEXTOS: Record<MotivoDeBloqueo, { titulo: string; detalle: string }> = {
  'sin-registrar': {
    titulo: 'Pantalla no disponible',
    detalle: 'Esta pantalla todavía no está habilitada. Si creés que debería estarlo, avisanos.',
  },
  rol: {
    titulo: 'Módulo no disponible',
    detalle:
      'Tu rol no tiene acceso a este módulo. Si creés que deberías tenerlo, contactá al administrador de tu empresa.',
  },
  'no-incluido': {
    titulo: 'Este módulo no está incluido',
    detalle:
      'Tu empresa no tiene contratado este módulo, así que no hay nada que tu administrador pueda habilitar. Si les interesa sumarlo, escribinos y lo vemos.',
  },
};

export function LockedModule({ motivo = 'rol' }: LockedModuleProps) {
  /*
    Recorrido 3-nov: «Volver al inicio» iba a `/`, que es `/dashboard`; si la empresa no
    tenía el Inicio, volvía a este mismo cartel. Ahora va a la primera pantalla que la
    empresa abre (`primera-pantalla.tsx`); si la cerrada ES esa, a Mi perfil.
  */
  const { pathname } = useLocation();
  const primera = usePrimeraPantalla();
  const inicio = !primera ? '/' : primera === pathname ? PANTALLA_SIN_MODULOS : primera;
  // Mini Prixia: a quien está probando no le falta un permiso, le falta el plan. X1:
  // vuelve al inicio de la prueba con el cuadro del plan completo abierto.
  if (motivo === 'no-incluido' && esEmpresaDemo()) return <VolverConElPlanCompleto />;
  const { titulo, detalle } = TEXTOS[motivo];
  // Un candado habla de permisos; una caja abierta, de algo que no se contrató.
  const Icono = motivo === 'no-incluido' ? PackageOpen : Lock;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="max-w-md space-y-4 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Icono className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-semibold">{titulo}</h1>
        <p className="text-muted-foreground">{detalle}</p>
        <Link
          to={inicio}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

export default LockedModule;
